<script lang="ts">
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';

  import type { HardwareVerticalSliderProps } from './types';

  export let entryId: HardwareVerticalSliderProps['entryId'];
  export let customCssClass: HardwareVerticalSliderProps['customCssClass'];
  export let images: HardwareVerticalSliderProps['images'];
  export let descriptions: HardwareVerticalSliderProps['descriptions'];

  let container: HTMLElement;
  let activeElementIdx: number = 0;
  let containerWidth: number = 0;

  const numOfColumns = descriptions.length;

  function updateContainerWidth() {
    containerWidth = container.offsetWidth;
  }

  function handleClick(idx: number) {
    activeElementIdx = idx;

    updateContainerWidth();
  }

  function handleKeydown(event: KeyboardEvent, idx: number) {
    if (event.key === 'Enter' || event.key === 'Space') {
      event.preventDefault();

      handleClick(idx);
    }
  }

  $: transformX = containerWidth ? (containerWidth / numOfColumns) * activeElementIdx + activeElementIdx * 10 : 0;
</script>

<svelte:window on:resize={updateContainerWidth} />

<div
  class="HardwareVerticalSlider HardwareVerticalSlider-{entryId}-box-properties {customCssClass}"
  data-entry-id={entryId}
  style="--columns: {numOfColumns};"
>
  <div class="HardwareVerticalSlider__container" bind:this={container}>
    <div class="HardwareVerticalSlider__subcontainer">
      {#each images as image, idx}
        <div
          class="HardwareVerticalSlider__img"
          class:is-active={idx === activeElementIdx}
          aria-hidden={idx === activeElementIdx ? 'false' : 'true'}
        >
          <DynamicChildrenComponent children={[image]} />
        </div>
      {/each}
      {#each descriptions as description, idx}
        <div
          class="HardwareVerticalSlider__text"
          class:is-active={idx === activeElementIdx}
          role="button"
          aria-pressed={idx === activeElementIdx}
          tabindex="0"
          on:click={() => handleClick(idx)}
          on:keydown={(event) => handleKeydown(event, idx)}
        >
          <DynamicChildrenComponent children={[description]} />
        </div>
      {/each}
    </div>
    <div
      class="HardwareVerticalSlider__progress"
      role="progressbar"
      aria-valuenow={activeElementIdx}
      aria-valuemin="0"
      aria-valuemax={descriptions.length - 1}
      aria-label="Progress"
      style="transform: translateX({transformX}px);"
    />
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/_media_queries' as media;

  .HardwareVerticalSlider {
    &__container {
      position: relative;

      &:after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        right: 0;

        height: 1px;

        background-color: var(--color-gray-15);

        @include media.until('medium') {
          content: normal;
        }
      }
    }

    &__subcontainer {
      display: grid;
      column-gap: var(--space-30);
      row-gap: var(--space-30);

      @include media.at('medium') {
        grid-template-columns: repeat(var(--columns), 1fr);
        row-gap: var(--space-40);
        padding-bottom: var(--space-30);
      }

      & > div {
        transition: all var(--duration-default) var(--easing-standard);

        &.is-active {
          --color: var(--color-text);

          opacity: 1 !important;
        }

        @for $i from 1 through 6 {
          &:nth-child(#{$i}) {
            @if $i <= 3 {
              order: 2 * $i - 1;
            } @else {
              order: 2 * ($i - 3);
            }
          }
        }
      }
    }

    &__img {
      @include media.at('medium') {
        grid-column: 1 / span var(--columns);
        grid-row: 1;

        opacity: 0;
      }
    }

    &__text {
      height: min-content;

      @include media.at('medium') {
        --color: var(--color-gray-55);

        cursor: pointer;
      }
    }

    &__progress {
      position: absolute;
      left: 0;
      bottom: 1px;

      height: 2px;
      width: calc((var(--container-width-current) - (var(--space-30) * 2)) / var(--columns));

      background-color: var(--color-blue);
      transition: transform var(--duration-default) var(--easing-standard);

      @include media.until('medium') {
        display: none;
      }
    }
  }
</style>
